// A Service vessel's entry form: its Entry form button (Service page) fills in, saves and prints the
// customer's Vessel Entry & Work Control Form (ΕΝΤΥΠΟ ΕΙΣΟΔΟΥ ΣΚΑΦΟΥΣ & ΕΛΕΓΧΟΥ ΕΡΓΑΣΙΩΝ). Its record is the
// boat yard record the Documents page keeps (boatDocs), kept with the vessel by its row id: the vessel's
// name, model, SAP, Arrival date (entryDate) and Handover Date (exitDate) are the record's own fields, the
// rest of the form its control part. This is the pure part: what a new form starts with (from the vessel's
// row on the Service sheet), which saved forms belong to which vessel, and which of them the button opens.
// The dialog is components/VesselEntryForm.tsx, the printout features/vesselEntryPrint.ts.

import {
  FUNCTIONAL_KEYS, MODEL_ORDER, VESSEL_ENTRY_LIMITS, VISUAL_KEYS, emptyVesselEntryControl, findServiceColumn, splitBoatTitle, VesselEntryControlSchema,
  type BoatDocRecord, type CheckResult, type FunctionalKey, type VesselEntryControl,
} from '@technohull/shared';

/** what a boat yard record is filled in with (the Documents form and a vessel's entry form). */
export interface BoatDocFields {
  boatName: string;
  model: string;
  sap: string;
  phone: string;
  hin: string;
  entryDate: string;
  exitDate: string;
  note: string;
}

/** a vessel's dates on the Service sheet, as 'YYYY-MM-DD' ('' when its cell is blank or the sheet has no
 *  such column). */
export interface VesselDates {
  /** "Expected date": when the boat is expected (the app's own column) */
  expected: string;
  /** "Start Date": when the works start */
  start: string;
  /** "Finish Date": when the works finish */
  finish: string;
  /** "Expected delivery date": when the boat is planned to go back */
  expectedDelivery: string;
  /** "Delivery date": when it went (or goes) back */
  delivery: string;
  /** "Estimated Vessel Arrival Date": when it is expected at the yard */
  expectedArrival: string;
  /** "Actual Vessel Arrival Date": when it came */
  arrival: string;
}
const NO_DATES: VesselDates = { expected: '', start: '', finish: '', expectedDelivery: '', delivery: '', expectedArrival: '', arrival: '' };

/** a Service vessel, as its entry form needs it. */
export interface EntryVessel {
  /** the Smartsheet id of the vessel's row: what its forms are kept with */
  rowId: number;
  /** the vessel's name, as the sheet has it */
  name: string;
  /** its SAP column */
  sap: string;
  /** the model its card shows under the name (the current fleet's boat with that SAP), if any */
  fleetModel?: string;
  /** its dates on the sheet (serviceVesselDates) */
  dates?: VesselDates;
}

/** the HIN registry (Vessel Identification) as the form reads it. */
export interface HinEntry { sap: string; hin: string; model?: string }

/** a sheet column, as the date matchers read it. */
export interface DateColumn { id: number; title: string; type?: string }

// ── the vessel's dates, found by column title (the way the Service page finds its columns) ──────────────
// "Expected date" is findServiceColumn('expected') (a plain "Expected"/"Expected date", never "Expected
// delivery date"); the others by what their titles say, so "Expected delivery date" and "Delivery date" are
// never mistaken for each other, and neither for "Expected date".
const EXPECT_RE = /expect|εκτιμ|αναμεν|αναμέν/i;
const DELIVERY_RE = /deliver|παράδοσ|παραδοσ/i;
const START_RE = /start|begin|έναρξ|εναρξ/i;
const FINISH_RE = /finish|\bend\b|complet|λήξ|ληξ|ολοκλ|τέλος/i;
const isDateCol = (c: DateColumn) => c.type === 'DATE' || c.type === 'DATETIME';

/** the leading 'YYYY-MM-DD' of a date cell (a DATETIME cell's day), else ''. */
const dayOf = (v: unknown): string => {
  const m = typeof v === 'string' ? /^\s*(\d{4}-\d{2}-\d{2})/.exec(v) : null;
  return m ? m[1]! : '';
};

/** the vessel's dates from its row on the Service sheet (cells by column id). */
export function serviceVesselDates(columns: readonly DateColumn[], cells: Readonly<Record<string, unknown>>): VesselDates {
  const dates = columns.filter(isDateCol);
  const expected = findServiceColumn('expected', dates);
  const expectedArrival = findServiceColumn('expectedArrival', dates);
  const arrival = findServiceColumn('arrival', dates);
  const own = new Set([expected?.id, expectedArrival?.id, arrival?.id]);
  const pick = (ok: (title: string) => boolean) => dates.find((c) => !own.has(c.id) && ok(c.title));
  const cols = {
    expected,
    expectedArrival,
    arrival,
    expectedDelivery: pick((t) => EXPECT_RE.test(t) && DELIVERY_RE.test(t)),
    delivery: pick((t) => DELIVERY_RE.test(t) && !EXPECT_RE.test(t)),
    start: pick((t) => START_RE.test(t) && !EXPECT_RE.test(t) && !DELIVERY_RE.test(t)),
    finish: pick((t) => FINISH_RE.test(t) && !EXPECT_RE.test(t) && !DELIVERY_RE.test(t)),
  };
  const at = (c: DateColumn | undefined) => (c ? dayOf(cells[String(c.id)]) : '');
  return {
    expected: at(cols.expected), start: at(cols.start), finish: at(cols.finish),
    expectedDelivery: at(cols.expectedDelivery), delivery: at(cols.delivery),
    expectedArrival: at(cols.expectedArrival), arrival: at(cols.arrival),
  };
}

// the digits of a SAP number, so "J9100391", "JS9100391" and "9100391" are the same boat (as on Service)
const sapKey = (s: string): string => String(s ?? '').match(/\d{5,}/)?.[0] ?? '';
/** the SAP as people write it: a number cell's "9100684.0" is 9100684. */
export const cleanSap = (s: string): string => s.trim().replace(/^(\d+)\.0+$/, '$1');

/** the vessel's line in the HIN registry, found by its SAP: one with a HIN first. */
function registryOf(sap: string, hins: readonly HinEntry[] | undefined): HinEntry | undefined {
  const key = sapKey(sap);
  if (!key || !hins) return undefined;
  const mine = hins.filter((h) => sapKey(h.sap) === key);
  return mine.find((h) => h.hin.trim()) ?? mine[0];
}

/** the model a vessel's name starts with ("GT9 · Θαλασσινή", "GT7_FANDY", "38_AIOLOS - THYELLA"), when it
 *  is one of the range. */
function modelInName(name: string): string {
  const m = splitBoatTitle(name).model.toUpperCase();
  return (MODEL_ORDER as readonly string[]).includes(m) ? m : '';
}

/** a new form: the record's own fields and the rest of the form. */
export interface VesselEntryDraft { fields: BoatDocFields; control: VesselEntryControl }

/** when and by whom a new form is filled in. */
export interface PrefillContext {
  /** today, 'YYYY-MM-DD' (local) */
  today: string;
  /** the signed-in person's name */
  me: string;
  /** the vessel's last Handover Date on a saved form ('YYYY-MM-DD'), if any: a sheet date up to then was
   *  that earlier visit's, not this one's */
  lastHandover?: string;
}

/** a new form for a vessel, filled in from its row on the Service sheet. Everything stays editable.
 *  - Vessel Name: its name as the sheet has it. Type / Model / SAP: the model its card shows (the fleet's,
 *    else the one its name starts with, else the HIN registry's) and its SAP (a number cell's ".0" dropped).
 *    The record also keeps the HIN the registry holds for that SAP (shown on Documents).
 *  - Arrival date: its Actual Vessel Arrival Date, else today.
 *  - Works Commenced: its Start Date. Estimated Completion: its Expected delivery date, else a Delivery date
 *    still to come, else its Finish Date. Handover Date: its Delivery date only once that day has come (a
 *    later one is a plan, not a handover, and would close the visit).
 *  - A boat that is back (an earlier visit's form was handed over): none of the sheet's dates on or before
 *    that handover is taken, as they were that visit's (the row still holds them until someone updates it).
 *  - Inspected by and Registered by: whoever fills it in, on today's date. Rolodex entry completed: ticked
 *    (it is being entered now).
 *  Each value is cut to what the record can hold. */
export function vesselEntryPrefill(v: EntryVessel, hins: readonly HinEntry[] | undefined, at: PrefillContext): VesselEntryDraft {
  const name = v.name.trim();
  const sap = cleanSap(v.sap);
  const reg = registryOf(sap, hins);
  const model = v.fleetModel?.trim() || modelInName(name) || reg?.model?.trim() || '';
  const all = v.dates ?? NO_DATES;
  // the sheet's dates for this visit: one on or before the last handover was the earlier visit's
  const thisVisit = (iso: string) => (iso && !(at.lastHandover && iso <= at.lastHandover) ? iso : '');
  const d: VesselDates = {
    expected: thisVisit(all.expected), start: thisVisit(all.start), finish: thisVisit(all.finish),
    expectedDelivery: thisVisit(all.expectedDelivery), delivery: thisVisit(all.delivery),
    expectedArrival: thisVisit(all.expectedArrival), arrival: thisVisit(all.arrival),
  };
  const handedOver = !!d.delivery && d.delivery <= at.today;
  const me = at.me.trim().slice(0, 120);
  return {
    fields: {
      boatName: name.slice(0, 160),
      model: model.slice(0, 80),
      sap: sap.slice(0, 40),
      phone: '',
      hin: (reg?.hin ?? '').trim().slice(0, 80),
      entryDate: d.arrival || at.today,
      exitDate: handedOver ? d.delivery : '',
      note: '',
    },
    control: {
      ...emptyVesselEntryControl(),
      inspectedBy: me,
      inspectedOn: at.today,
      rolodexEntry: true,
      registeredOn: at.today,
      registeredBy: me,
      worksCommenced: d.start,
      estimatedCompletion: d.expectedDelivery || (d.delivery > at.today ? d.delivery : '') || d.finish,
    },
  };
}

/** a saved record's fields, to fill the form with. */
export const fieldsOf = (r: BoatDocRecord): BoatDocFields => ({
  boatName: r.boatName, model: r.model, sap: r.sap, phone: r.phone, hin: r.hin, entryDate: r.entryDate, exitDate: r.exitDate, note: r.note,
});

/** a saved record's control part, every field there: a plain entry/exit record opens blank, and a value
 *  that no longer reads (a field the form has changed since) is left blank on its own, never the whole
 *  form, so saving it again can't wipe the rest. A form saved before the arrival check was revised keeps
 *  what still fits it (legacyArrivalCheck). */
export function controlOf(r: Pick<BoatDocRecord, 'control'>): VesselEntryControl {
  const out = emptyVesselEntryControl();
  const saved = r.control as Record<string, unknown> | null;
  if (!saved || typeof saved !== 'object') return out;
  const whole = VesselEntryControlSchema.safeParse(saved);
  if (whole.success) return whole.data;
  const shape = VesselEntryControlSchema.shape;
  const visual = shape.visual.removeDefault().shape;
  const functional = shape.functional.removeDefault().shape;
  for (const key of Object.keys(shape) as (keyof VesselEntryControl)[]) {
    if (key === 'visual' || key === 'functional') {
      const rows = (saved[key] ?? {}) as Record<string, unknown>;
      for (const k of key === 'visual' ? VISUAL_KEYS : FUNCTIONAL_KEYS) {
        const row = (key === 'visual' ? visual[k as keyof typeof visual] : functional[k as keyof typeof functional]).safeParse(rows[k]);
        if (row.success) (out[key] as Record<string, unknown>)[k] = row.data;
      }
      continue;
    }
    const v = shape[key].safeParse(saved[key]);
    if (v.success) (out as Record<string, unknown>)[key] = v.data;
  }
  return 'equipment' in saved && !('functional' in saved) ? legacyArrivalCheck(saved, out) : out;
}

// the earlier arrival check's rows that the basic functional check still has, and its results' new names
const LEGACY_ROWS: readonly FunctionalKey[] = ['chartplotter', 'gps', 'navLights', 'deckLights', 'bilgePumps'];
const LEGACY_RESULT: Record<string, CheckResult> = { ok: 'ok', fault: 'def', nt: 'nt' };

/** a form saved with the arrival check as it first was (an equipment check, Checked by, Date & time, the
 *  arrival photos box): its five equipment rows the functional check still has carry over (Fault is DEF,
 *  remarks the comment), Checked by is Inspected by, the check's day the Date, and a ticked arrival photos
 *  box Photos: Yes. What the revised check no longer has (ETA, VHF, bow thruster, trim tabs, the free-text
 *  boxes) is left out. */
function legacyArrivalCheck(saved: Record<string, unknown>, out: VesselEntryControl): VesselEntryControl {
  const str = (v: unknown, max: number) => (typeof v === 'string' ? v.slice(0, max) : '');
  const eq = (saved.equipment ?? {}) as Record<string, { result?: unknown; remarks?: unknown } | undefined>;
  const functional = { ...out.functional };
  for (const k of LEGACY_ROWS) {
    const row = eq[k];
    if (!row || typeof row !== 'object') continue;
    functional[k] = { result: LEGACY_RESULT[String(row.result)] ?? '', comment: str(row.remarks, VESSEL_ENTRY_LIMITS.comment) };
  }
  const checkedOn = str(saved.checkedAt, 10);
  return {
    ...out,
    functional,
    inspectedBy: out.inspectedBy || str(saved.checkedBy, VESSEL_ENTRY_LIMITS.name),
    inspectedOn: out.inspectedOn || (VesselEntryControlSchema.shape.inspectedOn.safeParse(checkedOn).data ?? ''),
    photos: out.photos || (saved.arrivalPhotos === true ? 'yes' : ''),
  };
}

/** the record is the Vessel Entry & Work Control Form (it has the control part): it prints that form, not
 *  the plain entry/exit one. */
export const isControlForm = (r: Pick<BoatDocRecord, 'control'>): boolean => r.control != null;

/** the Handover Date falls before the Actual Arrival: not one visit. A new form can start that way when the
 *  sheet's Delivery date is an earlier visit's (the boat is back), so the dialog says so and won't save it. */
export const handoverBeforeArrival = (f: Pick<BoatDocFields, 'entryDate' | 'exitDate'>): boolean =>
  !!f.entryDate.trim() && !!f.exitDate.trim() && f.exitDate < f.entryDate;

/** a form is closed once the boat has left (it has a handover / exit date); its next visit is a new form. */
export const isClosed = (r: Pick<BoatDocRecord, 'exitDate'>): boolean => !!r.exitDate.trim();

/** each vessel's saved forms (by row id), newest visit first: by entry date, else the day it was saved,
 *  then the newer record (the worker lists them the same way). Records with no vessel are left out. */
export function formsByVessel(records: readonly BoatDocRecord[]): Map<string, BoatDocRecord[]> {
  const day = (r: BoatDocRecord) => r.entryDate || r.createdAt;
  const out = new Map<string, BoatDocRecord[]>();
  for (const r of records) {
    if (!r.serviceRowId) continue;
    const list = out.get(r.serviceRowId);
    if (list) list.push(r);
    else out.set(r.serviceRowId, [r]);
  }
  for (const list of out.values()) list.sort((a, b) => (day(a) < day(b) ? 1 : day(a) > day(b) ? -1 : b.id - a.id));
  return out;
}

/** the latest Handover Date on the vessel's saved forms ('' when none was handed over). */
export const lastHandoverOf = (forms: readonly BoatDocRecord[]): string =>
  forms.reduce((last, f) => (isClosed(f) && f.exitDate > last ? f.exitDate : last), '');

/** the vessel's open form (the visit in progress), newest first if an edit ever left it two. */
export const openForm = (forms: readonly BoatDocRecord[], except?: number): BoatDocRecord | undefined =>
  forms.find((f) => !isClosed(f) && f.id !== except);

/** the form a vessel's button opens: the one asked for, else the open one (even when an older visit's form
 *  was given a later entry date), else its newest; none (a new form) when it has none, so pressing it
 *  again never starts a second form for the same visit. */
export function formToOpen(forms: readonly BoatDocRecord[], formId?: number): BoatDocRecord | null {
  return forms.find((f) => f.id === formId) ?? openForm(forms) ?? forms[0] ?? null;
}

/** a new form (the boat came back) is offered only once none of the vessel's forms is open. */
export const canStartNewVisit = (forms: readonly BoatDocRecord[]): boolean => !openForm(forms);
